/**
 * useDocumentActions Hook
 * Shared logic for document preview and download across all components.
 *
 * Both preview and download fetch the bytes through the Axios apiClient (so the
 * request carries auth + the X-Tenant-ID header) as a Blob, then use an in-memory
 * object URL. This is required because the frontend and API are on different hosts:
 * a raw browser request (<object data=...> / window.open) is NOT mediated by the
 * Axios interceptor, so it carries neither the tenant header nor the cross-origin
 * auth and the API rejects it with 401. Fetching as a Blob keeps the whole
 * round-trip authenticated and tenant-scoped, then a `blob:` object URL is handed
 * to the <img>/<object> preview or an <a download> for save. (Same pattern as the
 * client portal's ClientDocuments.handleDownload.)
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import apiClient from '../utils/apiClient';
import logger from '../utils/logger';

/**
 * Parse a filename out of a Content-Disposition header, tolerating the
 * `filename*=UTF-8''…` (RFC 5987) and quoted `filename="…"` forms.
 */
const fileNameFromDisposition = (disposition, fallback = 'download') => {
  if (!disposition) return fallback;
  const star = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (star?.[1]) {
    try { return decodeURIComponent(star[1]); } catch { /* fall through */ }
  }
  const plain = disposition.match(/filename="?([^";]+)"?/i);
  return plain?.[1] || fallback;
};

export default function useDocumentActions() {
  const [previewDocId, setPreviewDocId] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewType, setPreviewType] = useState(null);

  // Track the current preview object URL so we can revoke it before replacing it
  // or on close/unmount — otherwise each preview leaks a blob into memory.
  const previewObjectUrlRef = useRef(null);

  const revokePreviewObjectUrl = useCallback(() => {
    if (previewObjectUrlRef.current) {
      URL.revokeObjectURL(previewObjectUrlRef.current);
      previewObjectUrlRef.current = null;
    }
  }, []);

  /**
   * Open document in the in-page preview dialog. Fetches the bytes via the
   * authenticated Axios client (tenant header + auth attached) as a Blob and
   * renders it from an object URL.
   */
  const openPreview = useCallback(async (docId) => {
    if (!docId) return;
    try {
      const response = await apiClient.get(`/files/${docId}/stream`, {
        responseType: 'blob',
      });
      const blob = response.data;
      // Prefer the blob's own MIME type (set from the response Content-Type);
      // fall back to the response header so image-vs-PDF rendering is correct.
      const contentType =
        blob?.type || response.headers?.['content-type'] || 'application/octet-stream';

      revokePreviewObjectUrl();
      const objectUrl = URL.createObjectURL(blob);
      previewObjectUrlRef.current = objectUrl;

      setPreviewUrl(objectUrl);
      setPreviewType(contentType);
      setPreviewDocId(docId);
    } catch (err) {
      logger.error('Error loading document preview', {
        message: err.message,
        status: err.response?.status,
      });
    }
  }, [revokePreviewObjectUrl]);

  /**
   * Close the preview dialog and clean up state (revoking the object URL).
   */
  const closePreview = useCallback(() => {
    revokePreviewObjectUrl();
    setPreviewUrl(null);
    setPreviewType(null);
    setPreviewDocId(null);
  }, [revokePreviewObjectUrl]);

  /**
   * Download a document. Fetches the bytes via the authenticated Axios client as
   * a Blob (tenant header + auth attached), then triggers a browser save via a
   * temporary <a download>. Never relies on a raw browser request to the API.
   */
  const downloadDocument = useCallback(async (docId) => {
    if (!docId) return;
    try {
      const response = await apiClient.get(`/files/${docId}/download`, {
        responseType: 'blob',
      });
      const fileName = fileNameFromDisposition(response.headers?.['content-disposition']);
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      logger.error('Error downloading document', {
        message: err.message,
        status: err.response?.status,
      });
    }
  }, []);

  // Revoke any live preview object URL on unmount.
  useEffect(() => revokePreviewObjectUrl, [revokePreviewObjectUrl]);

  return {
    // State for DocumentPreviewDialog
    previewDocId,
    previewUrl,
    previewType,
    previewOpen: !!previewUrl,
    // Actions
    openPreview,
    closePreview,
    downloadDocument,
  };
}
